import React, { FormEvent, useCallback, useMemo, useState } from 'react';
import styles from './forgotPassword.module.scss';
import NewAuthPageLayout from '../../components/auth-page-layout/NewAuthPageLayout';
import { TextInput } from '../../components/text-input/TextInput';
import { NavLink, useHistory } from 'react-router-dom';
import { validateEmail } from '../../utils/validation';
import Button from '@src/components/button/Button';
import ArrowIcon from '../../assets/icons/curvearrow.svg?react';
import { useQueryParams } from '@src/hooks';
import { requestIdpV2PasswordReset } from '@src/utils/idpV2Auth';

type Props = {};

function ForgotPassword(props: Props) {
  const history = useHistory();
  const tokenExpired = useQueryParams().get('tokenExpired')
  const [email, setEmail] = useState('');
  const [completed, setCompleted] = useState(false);
  const [clientError, setClientError] = useState<undefined | string>();
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<undefined | string>();

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      return setClientError(error);
    }

    try {
      setLoading(true);
      setServerError(undefined);
      await requestIdpV2PasswordReset(email);
      setCompleted(true);
    } catch (e) {
      setServerError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [email]);

  const handleChange = useCallback((value) => {
    setClientError(undefined);
    setServerError(undefined);
    setEmail(value);
  }, [])

  const mainFormContent = useMemo(() => (
      <>
        <div className={styles.description}>
          Please enter your email address to receive a <span>Password Reset</span> link.
        </div>
        <TextInput allowSpecialCharacters placeholder={'Email'} label={'Email*'} id={'email'} value={email} onChange={handleChange}/>
        {!!clientError && <div className={styles.error}>{clientError}</div>}
        <div className={styles.back}>
          <NavLink className={styles.backLink} to="/login">
            Back to Login
          </NavLink>
        </div>
        <div className={styles.arrowDiv}>
          <span className={styles.arrowText}>Need help logging in?<br />Click to Chat with us!</span>
          <ArrowIcon width="40" height="200" />
        </div>
        <Button disabled={loading} className={styles.sendButton} label="Send Email" type="submit" isLoading={loading} />
      </>
    ),
    [clientError, email, handleChange, loading]
  );

  const formContent = useMemo(() => {
    if (completed && !serverError) {
      return (
        <>
          <div className={styles.confirm}>
            <div className={styles.confirmTitle}>Thank you!</div>
            <div className={styles.confirmSubtitle}>
              A Password Reset link has been emailed to you.<br /><br />
              Your link will <span className={styles.bold}>expire</span> after 60 minutes.<br /><br />
              If you do not receive an email, it may be because your email address is not registered to an account.
            </div>
          </div>
          <Button disabled={loading} className={styles.sendButton} label="BACK TO SIGN IN" isLoading={loading} onClick={() => history.push('/login') } />
        </>
      );
    }

    if (serverError) {
      return (
        <>
          <div className={styles.confirmError}>{serverError}</div>
          {mainFormContent}
        </>
      );
    }

    if (!completed && tokenExpired) {
      return (
        <>
          <div className={styles.confirmError}>
            Your Password Reset Link has Expired. Please generate a new link.
          </div>
          {mainFormContent}
        </>
      );
    }

    return <>{mainFormContent}</>;
  }, [completed, serverError, tokenExpired, mainFormContent, loading, history]);

  return (
    <NewAuthPageLayout backToLogin={true} title={<div>Reset your password</div>} buttonTitle={'Send'} loading={loading}
                    onSubmit={handleSubmit}>
      {formContent}
    </NewAuthPageLayout>
  );
}

export default ForgotPassword;
